import { createApi } from '@reduxjs/toolkit/query/react';

import {
  IAnnulPtaReportRequest,
  IPtaAnnulReason,
  IPtaReportAnnulResponse,
  IPtaReportHistory,
  IWorkItem,
} from '../types/IWorkItem';

import { axiosBaseQuery } from '@/redux/axios';
import { apiRoutes } from '@/api/apiRoutes';
import { devWarn } from '@/utils/logging';

export interface AnnulPtaReportArgs {
  id: number | string;
  body: IAnnulPtaReportRequest;
}

/**
 * The annul answer plus the work item as the server holds it afterwards.
 * `workItem` is null when that follow-up read failed: the annul itself has
 * already happened then, so it is not reported as an error.
 */
export interface AnnulPtaReportResult {
  response: IPtaReportAnnulResponse;
  workItem: IWorkItem | null;
}

export const ptaReportsApi = createApi({
  reducerPath: 'ptaReportsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['PtaAnnulReasons'],
  endpoints: (builder) => ({
    getPtaAnnulReasons: builder.query<IPtaAnnulReason[], void>({
      query: () => ({
        url: apiRoutes.getPtaAnnulReasons.path(),
        method: apiRoutes.getPtaAnnulReasons.method,
      }),
      providesTags: ['PtaAnnulReasons'],
    }),
    // No tag: the history is read only by the detail modal, which is mounted
    // only while open, so every open reads it fresh. Dropped from the cache as
    // soon as the modal closes, so a reopen never shows the previous entries.
    getPtaReportHistory: builder.query<IPtaReportHistory, number | string>({
      query: (id) => ({
        url: apiRoutes.getPtaReportHistoryByWorkItemId.path(id),
        method: apiRoutes.getPtaReportHistoryByWorkItemId.method,
      }),
      keepUnusedDataFor: 0,
    }),
    // No toast here: the confirmation modal renders the error itself.
    annulPtaReport: builder.mutation<AnnulPtaReportResult, AnnulPtaReportArgs>({
      async queryFn({ id, body }, _api, _extraOptions, baseQuery) {
        const annulled = await baseQuery({
          url: apiRoutes.annulPtaReport.path(id),
          method: apiRoutes.annulPtaReport.method,
          data: body,
        });
        if (annulled.error) {
          return { error: annulled.error };
        }

        // The annul answer carries only the annul fields; the full item is
        // what the live list and the open wizard reconcile against.
        const refreshed = await baseQuery({
          url: apiRoutes.getWorkItemById.path(id),
          method: apiRoutes.getWorkItemById.method,
        });
        if (refreshed.error) {
          devWarn(
            'Annulled PTA report, but could not read the work item back:',
            id,
            refreshed.error
          );
        }

        return {
          data: {
            response: annulled.data as IPtaReportAnnulResponse,
            workItem: refreshed.error ? null : (refreshed.data as IWorkItem),
          },
        };
      },
    }),
  }),
});

export const {
  useGetPtaAnnulReasonsQuery,
  useGetPtaReportHistoryQuery,
  useAnnulPtaReportMutation,
} = ptaReportsApi;
export default ptaReportsApi;
